import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { Shell } from '../components/Layout.js';
import { FolderDocs } from '../components/FolderDocs.js';
import { FolderMembers } from '../components/FolderMembers.js';
import { api, ApiError } from '../api/client.js';
import type { FolderRole } from '../api/client.js';
import { useAuth } from '../state/auth.js';
import { useT } from '../state/i18n.js';

// 엔터프라이즈 자료실 — 워크스페이스(테넌트) 도메인 전용 화면.
//
// 이 화면이 하는 일: 자료함(폴더) CRUD + 선택된 자료함의 문서/접근권한 패널 배치.
// 문서 목록·업로드·삭제는 <FolderDocs/>, 폴더 ACL 은 <FolderMembers/> 가 스스로 로드한다.
// 여기서는 "무엇을 선택했는가"와 "폴더 자체의 속성"만 관리하고, 패널 에러는 상단 배너 한 곳으로 모은다.
//
// 서버 계약(실측, apps/api orgs.controller.ts + org-membership.guard.ts):
//  - GET /orgs/current 는 public. tenant === null 이면 루트 도메인 → 이 기능 자체가 대상이 아니다.
//  - 테넌트 라우트의 "미로그인"은 401 이 아니라 **403**(OrgMembershipGuard 가 ForbiddenException).
//    그래서 로그인 여부는 상태 코드가 아니라 useAuth() 로 판정한다. 다만 403 두 가지를 섞으면 안 된다 —
//    'Sign in required for this workspace.'(세션 만료/미전달, 재로그인이면 해결) vs
//    'You are not a member of this workspace.'(진짜 멤버십 부재, 관리자 요청 필요).
//  - POST /projects 는 org ADMIN 이상. 클라이언트는 org 역할을 알 수 없으므로(=/orgs/current 가 안 준다)
//    폼은 항상 띄우고 서버 거부 메시지를 그대로 배너에 노출한다.
//  - PATCH/DELETE /projects/:id 는 폴더 ADMIN. 버튼 disabled 는 UX 힌트일 뿐 최종 판정은 서버.
//
// ★언어: 이 화면의 모든 유저 노출 문자열은 t({ko,en}) 로만 낸다(하드코딩 금지) —
//   기본 언어는 en(state/i18n.tsx initialLang). 라벨 맵도 {ko,en} 쌍으로 두고 렌더 시 해석한다.

type Visibility = 'org' | 'private';
type Phase = 'loading' | 'no-tenant' | 'signin' | 'no-access' | 'error' | 'ready';
type Tab = 'docs' | 'members';

type Dict = { ko: string; en: string };

interface Folder {
  id: string;
  code: string;
  name: string;
  createdAt: string;
  /** VIEWER 에게는 서버가 항상 null 로 준다(외부 키는 EDITOR+ 전용). */
  externalRef: string | null;
  externalRefKind: string | null;
  visibility: Visibility;
  myRole: FolderRole;
  docCount: number;
}

// ★orgId·name 등은 멤버에게만 온다(서버가 비멤버에겐 subdomain 만 준다 — 고객 명단 열거 차단).
interface Tenant {
  subdomain: string;
  orgId?: string;
  customDomain?: string | null;
  plan?: string;
  name?: string;
}

const ROLE_LABEL: Record<FolderRole, Dict> = {
  ADMIN: { ko: '관리자', en: 'Admin' },
  EDITOR: { ko: '편집', en: 'Editor' },
  VIEWER: { ko: '보기', en: 'Viewer' },
};
const ROLE_CLASS: Record<FolderRole, string> = { ADMIN: 'is-admin', EDITOR: 'is-editor', VIEWER: 'is-viewer' };

// 공개 범위 용어는 이 두 표에서만 나온다 — 알약("비공개")과 버튼("지정 멤버만")이 갈리면
// 같은 상태를 두 이름으로 부르게 되고, 사용자는 버튼이 상태 표시인지 동작인지 판단하지 못한다.
/** 현재 상태를 가리키는 명사 (알약·확인창). */
const VISIBILITY_LABEL: Record<Visibility, Dict> = {
  org: { ko: '조직 전체', en: 'Whole organization' },
  private: { ko: '지정 멤버만', en: 'Named members only' },
};
/** 누르면 벌어지는 일 (버튼). 키 = 전환 후 상태. */
const VISIBILITY_ACTION: Record<Visibility, Dict> = {
  org: { ko: '조직 전체 공개로 전환', en: 'Open to whole organization' },
  private: { ko: '지정 멤버만으로 전환', en: 'Restrict to named members' },
};

/** 테넌트 라우트의 403 중 "재로그인이면 풀리는" 쪽 (OrgMembershipGuard 의 미로그인 분기).
 *  ★1차 판정은 서버가 주는 안정 코드(payload.code)로 — 문구는 바뀌거나 i18n 될 수 있다.
 *  구버전 서버(코드 미제공) 호환용으로 문자열 매칭을 폴백으로만 남긴다. */
const SIGNIN_REQUIRED = /sign\s*in required/i;

/** ApiError 페이로드의 machine code 추출 (없으면 null). */
function codeOf(err: unknown): string | null {
  const payload = (err as { payload?: unknown })?.payload;
  if (payload && typeof payload === 'object' && 'code' in payload) {
    const code = (payload as { code?: unknown }).code;
    return typeof code === 'string' ? code : null;
  }
  return null;
}

/** 미로그인(재로그인이면 풀림) 403 인지 — 코드 우선, 문구 폴백. */
function isSigninRequired(err: unknown, detail: string): boolean {
  const code = codeOf(err);
  if (code) return code === 'signin_required';
  return SIGNIN_REQUIRED.test(detail);
}

/** 서버 메시지를 삼키지 않는다 — 정책 문구(중복 코드·권한 등)가 그대로 보여야 한다. */
function msgOf(err: unknown, fallback: string): string {
  if (err instanceof Error && err.message) return err.message;
  return fallback;
}

export function DocumentsPage(): JSX.Element {
  const auth = useAuth();
  const t = useT();
  const authLoading = auth.loading;
  const userId = auth.user?.id ?? null;

  const [tenant, setTenant] = useState<Tenant | null>(null);
  const [tenantChecked, setTenantChecked] = useState(false);
  const [phase, setPhase] = useState<Phase>('loading');
  /** 화면 전체를 대체하는 실패(게이트). banner 와 달리 복구는 '다시 시도'뿐. */
  const [fatal, setFatal] = useState<string | null>(null);
  /** 조작 실패 — 화면은 유지하고 상단 배너 한 곳에만 모은다(패널 onError 포함). */
  const [banner, setBanner] = useState<string | null>(null);

  const [folders, setFolders] = useState<Folder[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [tab, setTab] = useState<Tab>('docs');

  const [creating, setCreating] = useState(false);
  const [newCode, setNewCode] = useState('');
  const [newName, setNewName] = useState('');
  const [renaming, setRenaming] = useState(false);
  const [renameValue, setRenameValue] = useState('');
  const [busy, setBusy] = useState(false);

  // 연속 재조회에서 늦게 도착한 응답이 최신 목록을 덮지 않도록.
  const seq = useRef(0);
  // '다시 시도' — 전체 리로드 대신 마운트 시퀀스만 다시 태운다.
  const [retryKey, setRetryKey] = useState(0);

  const loadFolders = useCallback(async (): Promise<void> => {
    const my = ++seq.current;
    try {
      const res = await api.projects();
      if (my !== seq.current) return;
      setFolders(
        res.projects.map((p) => ({
          id: p.id,
          code: p.code,
          name: p.name || p.code,
          createdAt: p.createdAt,
          externalRef: p.externalRef,
          externalRefKind: p.externalRefKind,
          visibility: p.visibility,
          myRole: p.myRole,
          docCount: p.docCount,
        })),
      );
      setPhase('ready');
    } catch (err) {
      if (my !== seq.current) return;
      const status = err instanceof ApiError ? err.status : 0;
      if (status === 401) {
        // 테넌트 라우트에서는 현재 도달하지 않지만(가드가 403 을 던진다), 다른 계층이 401 을 주면 같은 처리.
        setFatal(null);
        setPhase('signin');
        return;
      }
      if (status === 403) {
        const detail = msgOf(err, '');
        // 403 두 종류를 한 화면으로 뭉개면, 재로그인만 하면 될 사람이 "멤버 추가해달라" 티켓을 넣는다.
        if (isSigninRequired(err, detail)) {
          setFatal(detail);
          setPhase('signin');
          return;
        }
        setFatal(
          detail ||
            t({ ko: '이 워크스페이스의 자료실에 접근할 권한이 없습니다.', en: "You don't have access to this workspace's library." }),
        );
        setPhase('no-access');
        return;
      }
      setFatal(msgOf(err, t({ ko: '자료함 목록을 불러오지 못했습니다.', en: 'Could not load folders.' })));
      setPhase('error');
    }
  }, [t]);

  // 1단계 — 테넌트 컨텍스트 확인. 루트 도메인이면 여기서 끝.
  useEffect(() => {
    let alive = true;
    api
      .currentOrg()
      .then((res) => {
        if (!alive) return;
        setTenant(res.tenant);
        setTenantChecked(true);
        if (!res.tenant) setPhase('no-tenant');
      })
      .catch((err: unknown) => {
        if (!alive) return;
        setTenantChecked(true);
        setFatal(msgOf(err, t({ ko: '워크스페이스 정보를 확인하지 못했습니다.', en: 'Could not verify workspace.' })));
        setPhase('error');
      });
    return () => {
      alive = false;
    };
  }, [retryKey, t]);

  // 2단계 — 테넌트가 있고 로그인 상태일 때만 자료함 목록.
  // deps 는 객체(tenant)가 아니라 subdomain 문자열 — 같은 내용의 새 응답이 와도 헛돌지 않고,
  // 같은 참조가 재사용돼도 retryKey 로 확실히 다시 탄다.
  // ★orgId 로 키를 잡으면 안 된다 — 비멤버·미로그인에겐 서버가 orgId 를 안 주므로
  //   signin/no-access 분기에 영영 못 들어가고 'loading' 에 갇힌다.
  const tenantKey = tenant?.subdomain ?? null;
  useEffect(() => {
    if (!tenantChecked || !tenantKey || authLoading) return;
    if (!userId) {
      setPhase('signin');
      return;
    }
    void loadFolders();
  }, [tenantChecked, tenantKey, authLoading, userId, loadFolders, retryKey]);

  // 선택 동기화 — 삭제/재조회로 사라진 선택은 첫 자료함으로 되돌린다.
  useEffect(() => {
    if (phase !== 'ready') return;
    setSelectedId((cur) => (cur && folders.some((f) => f.id === cur) ? cur : (folders[0]?.id ?? null)));
  }, [phase, folders]);

  useEffect(() => {
    setRenaming(false);
  }, [selectedId]);

  const mine = useMemo(() => folders.filter((f) => f.myRole !== 'VIEWER'), [folders]);
  const shared = useMemo(() => folders.filter((f) => f.myRole === 'VIEWER'), [folders]);
  const selected = useMemo(() => folders.find((f) => f.id === selectedId) ?? null, [folders, selectedId]);
  const isAdmin = selected?.myRole === 'ADMIN';

  function openCreate(): void {
    setBanner(null);
    setCreating(true);
  }

  async function submitCreate(event: FormEvent): Promise<void> {
    event.preventDefault();
    const code = newCode.trim();
    if (!code || busy) return;
    setBusy(true);
    setBanner(null);
    try {
      const res = await api.createProject(code, newName.trim() || code);
      setNewCode('');
      setNewName('');
      setCreating(false);
      await loadFolders();
      setSelectedId(res.project.id);
      setTab('docs');
    } catch (err) {
      setBanner(msgOf(err, t({ ko: '자료함을 만들지 못했습니다.', en: 'Could not create folder.' })));
    } finally {
      setBusy(false);
    }
  }

  function startRename(): void {
    if (!selected) return;
    setRenameValue(selected.name);
    setRenaming(true);
  }

  async function submitRename(event: FormEvent): Promise<void> {
    event.preventDefault();
    if (!selected || busy) return;
    const name = renameValue.trim();
    if (!name || name === selected.name) {
      setRenaming(false);
      return;
    }
    setBusy(true);
    setBanner(null);
    try {
      await api.updateProject(selected.id, { name });
      setRenaming(false);
      await loadFolders();
    } catch (err) {
      setBanner(msgOf(err, t({ ko: '이름을 변경하지 못했습니다.', en: 'Could not rename.' })));
    } finally {
      setBusy(false);
    }
  }

  // 공개 범위는 폴더 단위 판정이라, private → org 클릭 한 번이 이 자료함의 **모든 기존 문서**를
  // 조직 전체 열람으로 연다. 되돌려도 그 사이 열람·복사된 것은 되돌아오지 않으므로 확산 방향만 확인을 받는다.
  // (반대 방향 org → private 은 범위를 좁히는 쪽이라 즉시 실행.)
  async function toggleVisibility(): Promise<void> {
    if (!selected || busy) return;
    const next: Visibility = selected.visibility === 'org' ? 'private' : 'org';
    if (next === 'org') {
      const ok = window.confirm(
        t({
          ko:
            `"${selected.name}" 자료함을 ${VISIBILITY_LABEL.org.ko} 공개로 바꿉니다.\n\n` +
            `이 자료함의 문서 ${selected.docCount}건이 조직 전체에 공개됩니다. 계속할까요?`,
          en:
            `Change folder "${selected.name}" to ${VISIBILITY_LABEL.org.en}.\n\n` +
            `All ${selected.docCount} document(s) in this folder become visible to the whole organization. Continue?`,
        }),
      );
      if (!ok) return;
    }
    setBusy(true);
    setBanner(null);
    try {
      await api.updateProject(selected.id, { visibility: next });
      await loadFolders();
    } catch (err) {
      setBanner(msgOf(err, t({ ko: '공개 범위를 변경하지 못했습니다.', en: 'Could not change visibility.' })));
    } finally {
      setBusy(false);
    }
  }

  async function removeFolder(): Promise<void> {
    if (!selected || busy) return;
    const ok = window.confirm(
      t({
        ko:
          `"${selected.name}" 자료함을 삭제할까요?\n\n` +
          '안에 있던 문서는 지워지지 않고 “미분류”로 남습니다(공유 링크도 그대로 살아 있습니다).\n' +
          `대신 이 자료함의 접근 권한 설정과 식별자 “${selected.code}”는 사라지며, 되돌릴 수 없습니다.`,
        en:
          `Delete folder "${selected.name}"?\n\n` +
          'The documents inside are not deleted — they stay as “Uncategorized” (their share links keep working).\n' +
          `But this folder's access settings and the identifier “${selected.code}” are gone for good — this can't be undone.`,
      }),
    );
    if (!ok) return;
    setBusy(true);
    setBanner(null);
    try {
      await api.deleteProject(selected.id);
      setSelectedId(null);
      await loadFolders();
    } catch (err) {
      setBanner(msgOf(err, t({ ko: '자료함을 삭제하지 못했습니다.', en: 'Could not delete folder.' })));
    } finally {
      setBusy(false);
    }
  }

  // ── 게이트: 자료실 자체를 쓸 수 없는 상태 ────────────────────────────────
  if (phase === 'no-tenant') {
    return (
      <Gate
        title={t({
          ko: '자료실 — 팀 문서를 한곳에, 누가 어디까지 읽었는지까지',
          en: 'Library — your team’s documents in one place, with who-read-how-far',
        })}
      >
        <p>
          {t({
            ko: '자료실은 ',
            en: 'The Library is a ',
          })}
          <strong>{t({ ko: '워크스페이스(팀 공간)', en: 'workspace (team space)' })}</strong>
          {t({
            ko: ' 기능입니다. 제안서·보고서·자료를 자료함(폴더)으로 모으고, 멤버별 권한을 주고, 공유 링크로 보내면 ',
            en: ' feature. Collect proposals, reports, and files into folders, give per-member permissions, and send share links so you can track ',
          })}
          <strong>{t({ ko: '누가 열었고 어디서 멈췄는지', en: 'who opened it and where they stopped' })}</strong>
          {t({ ko: '까지 추적됩니다.', en: '.' })}
        </p>
        <ul className="docs-gate-list">
          <li>{t({ ko: '자료함(폴더)으로 문서 정리 — 리드·프로젝트 단위로 묶기', en: 'Organize documents into folders — by lead or project' })}</li>
          <li>{t({ ko: '멤버 권한 관리 / 편집 / 보기 (ADMIN · EDITOR · VIEWER)', en: 'Manage member permissions / edit / view (ADMIN · EDITOR · VIEWER)' })}</li>
          <li>{t({ ko: '열람 추적 — 누가, 언제, 어느 페이지까지 읽었는지', en: 'Read tracking — who, when, and how far they read' })}</li>
        </ul>
        <div className="docs-gate-cta">
          <Link to="/workspace" className="cta-primary">
            {t({ ko: '워크스페이스 만들기 (무료로 시작)', en: 'Create a workspace (start free)' })}
          </Link>
          <Link to="/plans" className="cta-secondary">
            {t({ ko: '요금제 보기', en: 'See plans' })}
          </Link>
        </div>
        <p className="docs-gate-sub">
          {t({ ko: '이미 회사 워크스페이스가 있나요? 그 주소(예: ', en: 'Already have a company workspace? Go to its address (e.g. ' })}
          <code>yourteam.cloudbtl.com</code>
          {t({ ko: ')로 접속하거나 워크스페이스 관리자에게 문의하세요.', en: ') or ask your workspace admin.' })}
        </p>
        <p className="docs-gate-sub">
          {t({
            ko: 'Pro·Business로 올리면 무만료 공유 링크 · SSO · 화이트라벨(내 브랜딩)까지 쓸 수 있습니다.',
            en: 'Upgrade to Pro or Business for non-expiring share links, SSO, and white-label (your own branding).',
          })}
        </p>
      </Gate>
    );
  }

  const tenantLabel = tenant ? (tenant.name ?? tenant.subdomain) : '';

  if (phase === 'signin') {
    return (
      <Gate title={t({ ko: '로그인이 필요합니다', en: 'Sign in required' })}>
        <p>
          {tenant
            ? t({
                ko: `${tenantLabel} 워크스페이스의 자료실입니다. 로그인 후 이용할 수 있습니다.`,
                en: `This is the ${tenantLabel} workspace library. Sign in to use it.`,
              })
            : t({ ko: '로그인 후 이용할 수 있습니다.', en: 'Sign in to continue.' })}
        </p>
        {fatal ? (
          <p className="docs-gate-sub" data-testid="docs-signin-detail">
            {t({
              ko: `로그인 세션이 만료됐거나 인증 정보가 전달되지 않았습니다. 다시 로그인하면 해결됩니다. (${fatal})`,
              en: `Your session expired or credentials weren't sent. Signing in again fixes it. (${fatal})`,
            })}
          </p>
        ) : null}
        <p className="docs-gate-sub">
          <Link to="/login" className="link">
            {t({ ko: '로그인하러 가기', en: 'Go to sign in' })}
          </Link>
        </p>
      </Gate>
    );
  }

  if (phase === 'no-access') {
    return (
      <Gate title={t({ ko: '접근 권한이 없습니다', en: 'No access' })}>
        <p>
          {tenant
            ? t({
                ko: `${tenantLabel} 워크스페이스의 멤버가 아니어서 자료실을 열 수 없습니다. 워크스페이스 관리자에게 멤버 추가를 요청하세요.`,
                en: `You're not a member of the ${tenantLabel} workspace, so you can't open its library. Ask the workspace admin to add you.`,
              })
            : t({
                ko: '멤버가 아니어서 자료실을 열 수 없습니다. 워크스페이스 관리자에게 멤버 추가를 요청하세요.',
                en: "You're not a member, so you can't open the library. Ask the workspace admin to add you.",
              })}
        </p>
        {fatal ? <p className="docs-gate-sub">{fatal}</p> : null}
        {/* 서버 메시지 분기를 믿더라도, 다른 계정으로 들어온 경우가 남는다 — 로그인 경로는 항상 병기. */}
        <p className="docs-gate-sub">
          {t({ ko: '다른 계정으로 들어오셨다면 ', en: 'Signed in with a different account? ' })}
          <Link to="/login" className="link">
            {t({ ko: '로그인 화면', en: 'the sign-in screen' })}
          </Link>
          {t({ ko: '에서 계정을 바꿔 다시 시도하세요.', en: ' lets you switch accounts and try again.' })}
        </p>
      </Gate>
    );
  }

  if (phase === 'error') {
    return (
      <Gate title={t({ ko: '자료실을 불러오지 못했습니다', en: 'Could not load the library' })}>
        <p>{fatal ?? t({ ko: '알 수 없는 오류가 발생했습니다.', en: 'An unknown error occurred.' })}</p>
        <p className="docs-gate-sub">
          <button
            type="button"
            className="docs-btn"
            data-testid="docs-retry"
            onClick={() => {
              setFatal(null);
              setPhase('loading');
              setRetryKey((k) => k + 1);
            }}
          >
            {t({ ko: '다시 시도', en: 'Try again' })}
          </button>
        </p>
      </Gate>
    );
  }

  const loading = phase === 'loading';

  return (
    <Shell>
      <section className="docs-page">
        <header className="docs-tenant" data-testid="docs-tenant">
          <div>
            <span className="docs-tenant-label">{t({ ko: '워크스페이스', en: 'Workspace' })}</span>
            <span className="docs-tenant-name">
              {tenant ? (tenant.name ?? tenant.subdomain) : t({ ko: '확인 중…', en: 'Checking…' })}
            </span>
          </div>
          {tenant ? (
            <span className="docs-tenant-meta">{tenant.customDomain ?? `${tenant.subdomain}.cloudbtl.com`}</span>
          ) : null}
        </header>

        {banner ? (
          <div className="docs-banner" role="alert" data-testid="docs-banner">
            <span className="docs-banner-text">{banner}</span>
            <button
              type="button"
              className="docs-banner-close"
              aria-label={t({ ko: '알림 닫기', en: 'Dismiss notice' })}
              onClick={() => setBanner(null)}
            >
              ✕
            </button>
          </div>
        ) : null}

        <div className="docs-layout">
          {/* ── 좌측: 자료함 목록 ── */}
          <aside className="docs-side" aria-label={t({ ko: '자료함 목록', en: 'Folder list' })}>
            <div className="docs-side-head">
              <h1 className="docs-side-title">{t({ ko: '자료실', en: 'Library' })}</h1>
              <div className="docs-side-tools">
                <button
                  type="button"
                  className="docs-btn docs-btn--sm"
                  onClick={() => void loadFolders()}
                  disabled={busy || loading}
                >
                  {t({ ko: '새로고침', en: 'Refresh' })}
                </button>
                <button
                  type="button"
                  className="docs-btn docs-btn--primary docs-btn--sm"
                  onClick={openCreate}
                  data-testid="docs-new"
                >
                  ＋ {t({ ko: '새 자료함', en: 'New folder' })}
                </button>
              </div>
            </div>

            {creating ? (
              <form className="docs-create" onSubmit={submitCreate} data-testid="docs-create-form">
                <label className="docs-field">
                  <span className="docs-field-label">{t({ ko: '식별자 (code)', en: 'Identifier (code)' })}</span>
                  <input
                    className="docs-input"
                    value={newCode}
                    autoFocus
                    maxLength={64}
                    placeholder={t({ ko: '예: acme-proposal', en: 'e.g. acme-proposal' })}
                    disabled={busy}
                    onChange={(e) => setNewCode(e.target.value)}
                  />
                </label>
                <p className="docs-create-hint">
                  {t({
                    ko: '식별자는 ',
                    en: 'The identifier is the key that groups ',
                  })}
                  <b>{t({ ko: '같은 식별자로 묶인 자료', en: 'documents sharing the same identifier' })}</b>
                  {t({
                    ko: '를 하나로 모으는 키입니다. 이 값으로 올라온 문서는 자동으로 이 자료함에 담기고, 외부 시스템 연동도 이 키를 기준으로 붙습니다. 만든 뒤에는 바꿀 수 없습니다.',
                    en: ' into one place. Documents uploaded with this value land in this folder automatically, and external integrations bind on this key. It can’t be changed after creation.',
                  })}
                </p>
                <label className="docs-field">
                  <span className="docs-field-label">{t({ ko: '이름', en: 'Name' })}</span>
                  <input
                    className="docs-input"
                    value={newName}
                    maxLength={200}
                    placeholder={t({ ko: '사람이 읽는 이름 (비우면 식별자 사용)', en: 'Human-readable name (defaults to the identifier)' })}
                    disabled={busy}
                    onChange={(e) => setNewName(e.target.value)}
                  />
                </label>
                <div className="docs-create-actions">
                  <button type="submit" className="docs-btn docs-btn--primary" disabled={busy || !newCode.trim()}>
                    {busy ? t({ ko: '만드는 중…', en: 'Creating…' }) : t({ ko: '만들기', en: 'Create' })}
                  </button>
                  <button type="button" className="docs-btn" disabled={busy} onClick={() => setCreating(false)}>
                    {t({ ko: '취소', en: 'Cancel' })}
                  </button>
                </div>
              </form>
            ) : null}

            {loading ? (
              <p className="docs-dim" role="status">
                {t({ ko: '불러오는 중…', en: 'Loading…' })}
              </p>
            ) : folders.length === 0 ? (
              <div className="docs-side-empty" data-testid="docs-side-empty">
                <p className="docs-dim">{t({ ko: '아직 자료함이 없습니다.', en: 'No folders yet.' })}</p>
                <button
                  type="button"
                  className="docs-btn docs-btn--primary"
                  onClick={openCreate}
                  data-testid="docs-first-folder"
                >
                  {t({ ko: '첫 자료함 만들기', en: 'Create your first folder' })}
                </button>
              </div>
            ) : (
              <>
                <FolderGroup
                  testId="docs-group-mine"
                  label={t({ ko: '내 자료함', en: 'My folders' })}
                  hint={t({ ko: '관리·편집', en: 'Manage · edit' })}
                  emptyLabel={t({ ko: '없음', en: 'None' })}
                  folders={mine}
                  selectedId={selectedId}
                  onSelect={setSelectedId}
                />
                <FolderGroup
                  testId="docs-group-shared"
                  label={t({ ko: '공유받은 자료함', en: 'Shared with me' })}
                  hint={t({ ko: '보기 전용', en: 'View only' })}
                  emptyLabel={t({ ko: '없음', en: 'None' })}
                  folders={shared}
                  selectedId={selectedId}
                  onSelect={setSelectedId}
                />
              </>
            )}
          </aside>

          {/* ── 우측: 선택된 자료함 ── */}
          <section className="docs-main">
            {!selected ? (
              <div className="docs-empty" data-testid="docs-main-empty">
                {folders.length === 0
                  ? t({ ko: '자료함을 만들면 여기에 문서와 접근 권한이 표시됩니다.', en: 'Create a folder to see its documents and access here.' })
                  : t({ ko: '왼쪽에서 자료함을 선택하세요.', en: 'Select a folder on the left.' })}
              </div>
            ) : (
              <>
                <header className="docs-folder-head">
                  <div className="docs-folder-ident">
                    {renaming ? (
                      <form className="docs-rename" onSubmit={submitRename}>
                        <input
                          className="docs-input"
                          value={renameValue}
                          autoFocus
                          maxLength={200}
                          aria-label={t({ ko: '자료함 이름', en: 'Folder name' })}
                          disabled={busy}
                          onChange={(e) => setRenameValue(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Escape') setRenaming(false);
                          }}
                        />
                        <button type="submit" className="docs-btn docs-btn--sm" disabled={busy}>
                          {busy ? t({ ko: '저장 중…', en: 'Saving…' }) : t({ ko: '저장', en: 'Save' })}
                        </button>
                        <button
                          type="button"
                          className="docs-btn docs-btn--sm"
                          disabled={busy}
                          onClick={() => setRenaming(false)}
                        >
                          {t({ ko: '취소', en: 'Cancel' })}
                        </button>
                      </form>
                    ) : (
                      <div className="docs-titlerow">
                        <h2 className="docs-folder-title">{selected.name}</h2>
                        <RolePill role={selected.myRole} />
                        <VisibilityPill visibility={selected.visibility} />
                      </div>
                    )}

                    <div className="docs-ref">
                      <span className="docs-ref-kind">{t({ ko: '식별자', en: 'Identifier' })}</span>
                      <code className="docs-ref-val">{selected.code}</code>
                      {selected.externalRef ? (
                        <>
                          <span className="docs-ref-kind">{selected.externalRefKind ?? t({ ko: '연결', en: 'Link' })}</span>
                          <code className="docs-ref-val" data-testid="docs-external-ref">
                            {selected.externalRef}
                          </code>
                        </>
                      ) : selected.myRole === 'VIEWER' ? (
                        // 서버가 VIEWER 에게는 externalRef 를 항상 null 로 마스킹한다(orgs.service canSeeRef).
                        // 그래서 여기서 "없음"이라고 단정하면 연동된 자료함을 미연동으로 오판시킨다.
                        <span
                          className="docs-dim"
                          data-testid="docs-external-ref-hidden"
                          title={t({ ko: '외부 시스템 참조키는 편집 권한 이상만 볼 수 있습니다', en: 'External reference keys are visible only to Editor and above' })}
                        >
                          {t({ ko: '외부 연결 식별자 — 표시 권한 없음', en: 'External reference — you can’t view it' })}
                        </span>
                      ) : (
                        <span className="docs-dim" data-testid="docs-external-ref-none">
                          {t({ ko: '외부 시스템에 연결된 식별자 없음', en: 'No external system reference' })}
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="docs-actions">
                    <button
                      type="button"
                      className="docs-btn"
                      disabled={!isAdmin || busy || renaming}
                      title={isAdmin ? undefined : t({ ko: '자료함 관리자만 바꿀 수 있습니다', en: 'Only a folder admin can change this' })}
                      onClick={startRename}
                    >
                      {t({ ko: '이름 변경', en: 'Rename' })}
                    </button>
                    {/* 라벨은 현재 상태가 아니라 동작 — 상태는 옆 알약(VisibilityPill)이 이미 말한다. */}
                    <button
                      type="button"
                      className="docs-btn"
                      disabled={!isAdmin || busy}
                      title={
                        !isAdmin
                          ? t({ ko: '자료함 관리자만 바꿀 수 있습니다', en: 'Only a folder admin can change this' })
                          : selected.visibility === 'org'
                            ? t({ ko: '접근 권한에 지정된 멤버만 볼 수 있도록 좁힙니다', en: 'Restrict so only members named in access can view' })
                            : t({
                                ko: `이 자료함의 문서 ${selected.docCount}건이 조직 멤버 누구나 열람 가능해집니다`,
                                en: `All ${selected.docCount} document(s) here become viewable by any org member`,
                              })
                      }
                      onClick={() => void toggleVisibility()}
                      data-testid="docs-visibility"
                    >
                      {selected.visibility === 'org'
                        ? t(VISIBILITY_ACTION.private)
                        : t(VISIBILITY_ACTION.org)}
                    </button>
                    <button
                      type="button"
                      className="docs-btn docs-btn--danger"
                      disabled={!isAdmin || busy}
                      title={isAdmin ? undefined : t({ ko: '자료함 관리자만 삭제할 수 있습니다', en: 'Only a folder admin can delete this' })}
                      onClick={() => void removeFolder()}
                      data-testid="docs-delete"
                    >
                      {t({ ko: '삭제', en: 'Delete' })}
                    </button>
                  </div>
                </header>

                <div className="docs-tabs" role="tablist" aria-label={t({ ko: '자료함 보기', en: 'Folder views' })}>
                  <button
                    type="button"
                    role="tab"
                    aria-selected={tab === 'docs'}
                    className={`docs-tab${tab === 'docs' ? ' is-active' : ''}`}
                    onClick={() => setTab('docs')}
                  >
                    {t({ ko: '문서', en: 'Documents' })} <span className="docs-tab-count">{selected.docCount}</span>
                  </button>
                  <button
                    type="button"
                    role="tab"
                    aria-selected={tab === 'members'}
                    className={`docs-tab${tab === 'members' ? ' is-active' : ''}`}
                    onClick={() => setTab('members')}
                  >
                    {t({ ko: '접근 권한', en: 'Access' })}
                  </button>
                </div>

                {tab === 'docs' ? (
                  <FolderDocs
                    key={`docs-${selected.id}`}
                    projectId={selected.id}
                    projectCode={selected.code}
                    myRole={selected.myRole}
                    onError={setBanner}
                  />
                ) : (
                  <FolderMembers
                    key={`members-${selected.id}`}
                    projectId={selected.id}
                    myRole={selected.myRole}
                    onError={setBanner}
                  />
                )}
              </>
            )}
          </section>
        </div>
      </section>
    </Shell>
  );
}

function Gate({ title, children }: { title: string; children: React.ReactNode }): JSX.Element {
  return (
    <Shell>
      <section className="docs-gate" data-testid="docs-gate">
        <h1>{title}</h1>
        {children}
      </section>
    </Shell>
  );
}

function FolderGroup({
  testId,
  label,
  hint,
  emptyLabel,
  folders,
  selectedId,
  onSelect,
}: {
  testId: string;
  label: string;
  hint: string;
  emptyLabel: string;
  folders: Folder[];
  selectedId: string | null;
  onSelect: (id: string) => void;
}): JSX.Element {
  return (
    <div className="docs-group" data-testid={testId}>
      <div className="docs-group-head">
        <span className="docs-group-label">{label}</span>
        <span className="docs-group-hint">{hint}</span>
      </div>
      {folders.length === 0 ? (
        <p className="docs-group-empty">{emptyLabel}</p>
      ) : (
        <ul className="docs-folder-list">
          {folders.map((f) => (
            <li key={f.id}>
              <button
                type="button"
                className={`docs-folder${selectedId === f.id ? ' is-active' : ''}`}
                aria-current={selectedId === f.id ? 'true' : undefined}
                onClick={() => onSelect(f.id)}
              >
                <span className="docs-folder-main">
                  <span className="docs-folder-name">{f.name}</span>
                  <span className="docs-folder-code">{f.code}</span>
                </span>
                <span className="docs-folder-count">{f.docCount}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function RolePill({ role }: { role: FolderRole }): JSX.Element {
  const t = useT();
  return <span className={`docs-pill ${ROLE_CLASS[role]}`}>{t(ROLE_LABEL[role])}</span>;
}

function VisibilityPill({ visibility }: { visibility: Visibility }): JSX.Element {
  const t = useT();
  return (
    <span
      className="docs-pill is-muted"
      title={
        visibility === 'org'
          ? t({ ko: '워크스페이스 멤버 누구나 볼 수 있습니다', en: 'Any workspace member can view' })
          : t({ ko: '접근 권한에 지정된 멤버만 볼 수 있습니다', en: 'Only members named in access can view' })
      }
    >
      {t(VISIBILITY_LABEL[visibility])}
    </span>
  );
}
